import { Typography } from '@mui/material';
import React from 'react';
import HeroImageBlockEditor from './HeroImageBlockEditor';
import ImageCarouselBlockEditor from './ImageCarouselBlockEditor';
import LeftRightListBlockEditor from './LeftRightListBlockEditor';
import StandardHeaderBlockEditor from './StandardHeaderBlockEditor';
import SubtitleBlockEditor from './SubtitleBlockEditor';
import TableBlockEditor from './TableBlockEditor';
import TextBlockEditor from './TextBlockEditor';
import type {
  ContentItem,
  HeroImageType,
  ImageCarouselType,
  LeftRightListType,
  StandardHeaderTextType,
  SubtitleType,
  TableType,
  TextType,
} from '../../../models/ContentManagementInterface';
import { ContentBlockEditorProps } from './newContentManagementEditorTypes';

const withProperties =
  (block: ContentItem, onChange: (updated: ContentItem) => void) => (properties: unknown) =>
    onChange({ ...block, properties } as ContentItem);

type BlockEditorRenderProps = ContentBlockEditorProps & {
  libraryFundId: string;
  updateProperties: (properties: unknown) => void;
};

function StandardHeaderEditor(props: BlockEditorRenderProps) {
  return (
    <StandardHeaderBlockEditor
      block={props.block as StandardHeaderTextType}
      onChange={
        props.updateProperties as (properties: StandardHeaderTextType['properties']) => void
      }
      footnotesOpen={props.footnotesOpen}
    />
  );
}

function LeftRightEditor(props: BlockEditorRenderProps) {
  return (
    <LeftRightListBlockEditor
      block={props.block as LeftRightListType}
      onChange={props.updateProperties as (properties: LeftRightListType['properties']) => void}
      footnotesOpen={props.footnotesOpen}
    />
  );
}

function ImageCarouselEditor(props: BlockEditorRenderProps) {
  return (
    <ImageCarouselBlockEditor
      block={props.block as ImageCarouselType}
      onChange={props.updateProperties as (properties: ImageCarouselType['properties']) => void}
      modelId={props.modelId}
      modelType={props.modelType ?? 'FundOpportunity'}
      fundOpportunityId={props.libraryFundId}
      backendService={props.backendService}
      autoImportFile={props.autoImportFile}
      onAutoImportConsumed={props.onAutoImportConsumed}
      primeCardsAdjust={props.primeCardsAdjust}
      onPrimeCardsAdjustConsumed={props.onPrimeCardsAdjustConsumed}
      footnotesOpen={props.footnotesOpen}
    />
  );
}

function TableEditor(props: BlockEditorRenderProps) {
  return (
    <TableBlockEditor
      block={props.block as TableType}
      onChange={props.updateProperties as (properties: TableType['properties']) => void}
      modelId={props.modelId}
      modelType={props.modelType ?? 'FundOpportunity'}
      fundOpportunityId={props.libraryFundId}
      backendService={props.backendService}
      footnotesOpen={props.footnotesOpen}
    />
  );
}

export default function ContentBlockEditor(editorProps: ContentBlockEditorProps) {
  const { block, onChange, modelId, fundOpportunityId, backendService } = editorProps;
  const modelType = editorProps.modelType ?? 'FundOpportunity';
  const updateProperties = withProperties(block, onChange);
  const props = {
    ...editorProps,
    modelType,
    libraryFundId: fundOpportunityId ?? modelId,
    updateProperties,
  };
  switch (block.type) {
    case 'StandardHeaderText':
      return <StandardHeaderEditor {...props} />;
    case 'Subtitle':
      return (
        <SubtitleBlockEditor
          block={block as SubtitleType}
          onChange={updateProperties as (properties: SubtitleType['properties']) => void}
        />
      );
    case 'Text':
      return (
        <TextBlockEditor
          block={block as TextType}
          onChange={updateProperties as (properties: TextType['properties']) => void}
        />
      );
    case 'HeroImage':
      return (
        <HeroImageBlockEditor
          block={block as HeroImageType}
          onChange={updateProperties as (properties: HeroImageType['properties']) => void}
          modelId={modelId}
          modelType={modelType}
          backendService={backendService}
          primeCardsAdjust={editorProps.primeCardsAdjust}
          onPrimeCardsAdjustConsumed={editorProps.onPrimeCardsAdjustConsumed}
        />
      );
    case 'LeftRightList':
      return <LeftRightEditor {...props} />;
    case 'ImageCarousel':
      return <ImageCarouselEditor {...props} />;
    case 'Table':
      return <TableEditor {...props} />;
    default:
      return (
        <Typography color="text.secondary" variant="body2">
          Unsupported block type.
        </Typography>
      );
  }
}
